import { create } from "zustand";
import { ApiInvites } from "../api/invites/api-invites";
import { type Invite } from "../api/invites/types";
import { useToastStore } from "./toastStore";
import { useSuppliersStore } from "./suppliersStore";
import { useUiStore } from "./uiStore";

type ActionType = "accept" | "decline" | null;

interface InvitesState {
  invites: Invite[];
  currentInviteIndex: number;
  loadingAction: ActionType;
  fetchInvites: () => Promise<void>;
  acceptCurrentInvite: () => Promise<void>;
  declineCurrentInvite: () => Promise<void>;
  proceedToNextInvite: () => void;
  closeModalAndReset: () => void;
}

let invitesFetched = false;
const apiInvites = new ApiInvites();

export const useInvitesStore = create<InvitesState>((set, get) => ({
  invites: [],
  currentInviteIndex: 0,
  loadingAction: null,

  fetchInvites: async () => {
    if (invitesFetched) return;
    invitesFetched = true;

    try {
      const response = await apiInvites.getInvites();
      const waitingInvites = response.data.data.filter(
        (invite) => invite.status === "WAITING",
      );

      if (waitingInvites.length > 0) {
        set({
          invites: waitingInvites,
          currentInviteIndex: 0,
        });
        useUiStore.getState().openModal("INVITE", {});
      }
    } catch (error) {
      console.error("Failed to fetch invites:", error);
      invitesFetched = false;
    }
  },

  proceedToNextInvite: () => {
    const { invites, currentInviteIndex } = get();
    if (currentInviteIndex < invites.length - 1) {
      set({ currentInviteIndex: currentInviteIndex + 1 });
    } else {
      get().closeModalAndReset();
    }
  },

  acceptCurrentInvite: async () => {
    const { invites, currentInviteIndex } = get();
    const currentInvite = invites[currentInviteIndex];
    if (!currentInvite) return;

    set({ loadingAction: "accept" });

    try {
      await apiInvites.acceptInvite(currentInvite.id);
      useToastStore.getState().showToast("Приглашение принято!", "success");
      await useSuppliersStore.getState().fetchSuppliers();
      get().proceedToNextInvite();
    } catch (error) {
      console.error("Failed to accept invite:", error);
      useToastStore
        .getState()
        .showToast("Не удалось принять приглашение", "error");
    } finally {
      set({ loadingAction: null });
    }
  },

  declineCurrentInvite: async () => {
    const { invites, currentInviteIndex } = get();
    const currentInvite = invites[currentInviteIndex];
    if (!currentInvite) return;

    set({ loadingAction: "decline" });

    try {
      await apiInvites.declineInvite(currentInvite.id);
      useToastStore.getState().showToast("Приглашение отклонено", "info");
      get().proceedToNextInvite();
    } catch (error) {
      console.error("Failed to decline invite:", error);
      useToastStore
        .getState()
        .showToast("Не удалось отклонить приглашение", "error");
    } finally {
      set({ loadingAction: null });
    }
  },

  closeModalAndReset: () => {
    useUiStore.getState().closeModal();
    set({
      invites: [],
      currentInviteIndex: 0,
      loadingAction: null,
    });
    invitesFetched = false;
  },
}));
